import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../../../shared/src/lib/services/bet-placement.service";
import { AsyncPipe, DecimalPipe, NgClass, NgStyle } from "@angular/common";
import { CoreService } from "../../../../../../../../../shared/src/lib/services/core.service";
import { NavService } from "../../../../../../../../../shared/src/lib/services/nav.service";
import { PnlCalculatorService } from "../../../../../../../../../shared/src/lib/services/pnl-calculator.service";
import { ExposureService } from "../../../../../../../../../shared/src/lib/services/exposure.service";
import { Router } from "@angular/router";
import { safeParseFloat } from "../../../../../../../../../shared/src/lib/utils/common";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { ExRateService } from "../../../../../../../../../shared/src/lib/services/ex-rate.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "[app-runner-td]",
  imports: [NgClass, DecimalPipe, AsyncPipe],
  templateUrl: "./runner-td.component.html",
  styleUrl: "./runner-td.component.scss",
})
export class RunnerTdComponent implements OnInit, OnDestroy, OnChanges {
  @Input() market: any;
  @Input() data: any;
  @Input() update: any = null;
  @Input() side: any;
  placementService = inject(BetPlacementService);
  coreService = inject(CoreService);
  exRateService = inject(ExRateService);
  navService = inject(NavService);
  pnlCalculator = inject(PnlCalculatorService);
  exposureService = inject(ExposureService);
  router = inject(Router);
  breakpointObserver = inject(BreakpointObserver);
  cell = {
    price: 0,
    size: 0,
    changed: false,
  };
  priceChangeFlash: boolean = false;
  showSize: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  flashTimeoutId: any = null;
  readonly destroy$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.["update"]) {
      const cellUpdate = changes?.["update"]?.currentValue;
      // console.log("Cell update", cellUpdate);
      if (cellUpdate) this.handleChanges(cellUpdate);
    }
  }

  ngOnInit() {
    this.getNetEffect();
    this.setFlashingRule();
    this.bpObserver();

    this.placementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "slipChange") {
        this.setFlashingRule();
      }
    });
  }

  bpObserver() {
    this.breakpointObserver
      .observe(["(min-width: 1281px) and (max-width: 1419px)"])
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: BreakpointState) => {
        this.showSize.next(!state.matches);
      });
  }

  setFlashingRule() {
    const slipSettings = this.placementService.getSlipSettings();
    this.priceChangeFlash = slipSettings?.betSlipSettings?.flashOnPriceChange === "enable";
  }

  handleChanges(updateData: any) {
    let newPrice;
    let newSize;
    if (this.side === "back") {
      newPrice = safeParseFloat(updateData?.abp?.[0]) ?? 0;
      newSize = safeParseFloat(updateData?.abs?.[0]) ?? 0;
    } else {
      newPrice = safeParseFloat(updateData?.alp?.[0]) ?? 0;
      newSize = safeParseFloat(updateData?.als?.[0]) ?? 0;
    }
    const priceChanged = this.cell.price !== newPrice;
    const sizeChanged = this.cell.size !== newSize;

    if (priceChanged || sizeChanged) {
      this.cell.changed = true;

      if (this.flashTimeoutId) clearTimeout(this.flashTimeoutId);

      this.flashTimeoutId = setTimeout(() => {
        this.cell.changed = false;
        this.flashTimeoutId = null;
      }, 2000);
    }

    if (priceChanged) this.cell.price = newPrice;
    if (sizeChanged) this.cell.size = newSize;
  }

  handleSelect() {
    if (this.cell?.price === 0) return;
    this.navService.openBetSlipBar.next(true);
    const data = {
      F_ODDS: this.cell?.price,
      PROVIDER: "BF",
      PROVIDER_ID: "0",
      SERVICE_ID: "0",
      PRICE: this.cell?.price,
      SIZE: this.cell?.size,
      SIDE: this.side?.toUpperCase(),
      MARKET_ID: this.market?.marketId,
      SPORT_ID: this.market?.eventType?.id,
      EVENT_ID: this.market?.event?.id,
      PERSISTENCE_TYPE: this.placementService.persistenceType,
      marketName: this.market?.marketName,
      eventName: this.market?.event?.name,
      SELECTION_ID: this.data?.selectionId,
      HANDICAP: this.data?.handicap,
      runnerName: this.data?.runnerName,
      runners: this.market.runners,
      context: "match-odds",
    };
    this.placementService.setParams({
      type: this.market?.description?.bettingType,
      data,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.flashTimeoutId) {
      clearTimeout(this.flashTimeoutId);
    }
  }

  getNetEffect() {
    this.pnlCalculator
      .getNetEffect(this.market?.marketId, this.data?.selectionId, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result < 0 && this.market?.marketId && this.data?.selectionId) {
          if (this.exposureService.currentMarketExposures[this.market.marketId]) {
            this.exposureService.currentMarketExposures[this.market.marketId] = [...this.exposureService.currentMarketExposures[this.market.marketId], result];
          } else {
            this.exposureService.currentMarketExposures[this.market.marketId] = [result];
          }
        }
      });
    this.pnlCalculator
      .getPotentialNetEffect(this.market?.marketId, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result !== null && result < 0 && this.data?.selectionId) {
          this.exposureService.predictedMarketExposure[this.data?.selectionId] = result;
        }
      });
  }
}
